'use client';

import { type CSSProperties } from 'react';

import type { RenderElementProps } from 'slate-react';

import { SlateEditorLink } from '../../channel-message-editor/elements/SlateEditorLink';
import { SlateEditorMention } from '../../channel-message-editor/elements/SlateEditorMention';
import type { AlignType, CustomElement } from '../types/SlateNodes';

/**
 * Slate `renderElement` dispatcher for the composer. Honors
 * `align` on block elements and routes inline `link` / `mention`
 * to their dedicated components.
 */
export function SlateEditorElement(props: RenderElementProps) {
  const { attributes, children, element } = props;
  // Cast through `unknown` — Slate's Element type is augmented in
  // another workspace slice so a direct cast errors at the cross-
  // slice boundary even though the shape matches at runtime.
  const node = element as unknown as CustomElement;
  const style: CSSProperties = {};
  if ('align' in node && node.align) {
    style.textAlign = node.align as AlignType;
  }

  switch (node.type) {
    case 'block-quote':
      return (
        <blockquote
          style={style}
          {...attributes}
          className="border-line-strong text-content-2 my-1 border-l-2 pl-3 italic"
        >
          {children}
        </blockquote>
      );
    case 'bulleted-list':
      return (
        <ul style={style} {...attributes} className="my-1 list-disc pl-5">
          {children}
        </ul>
      );
    case 'numbered-list':
      return (
        <ol style={style} {...attributes} className="my-1 list-decimal pl-5">
          {children}
        </ol>
      );
    case 'list-item':
      return <li {...attributes}>{children}</li>;
    case 'link':
      return <SlateEditorLink {...props} />;
    case 'mention':
      return <SlateEditorMention {...props} />;
    case 'paragraph':
    default:
      return (
        <p style={style} {...attributes}>
          {children}
        </p>
      );
  }
}
